import {
  Box,
  Button,
  Checkbox,
  Container,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import NiceModal from '@ebay/nice-modal-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isError } from 'lodash';

import { getJamClient } from '@/src/api/sdk/JamClient';
import { useAuth } from '@/src/store/auth.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { preProdLogger } from '@/src/utils/log.utils';

import { ChallengeValidationState, ChallengeValidationStatus } from '@amzn/aws-jam-typescript-client';

import { ChallengeValidationDetailModal } from './ChallengeValidationDetailModal';
import AutoRefreshDropdown from '@/src/components/common/AutoRefreshDropdown/AutoRefreshDropdown';

import { Challenge } from '@/src/types/Challenge';

interface ChallengeValidationProps {
  challenge: Challenge;
}

const isTerminalStatus = (status: ChallengeValidationStatus | string): boolean => {
  return (
    status === ChallengeValidationStatus.PASSED ||
    status === ChallengeValidationStatus.FAILED ||
    status === ChallengeValidationStatus.ERROR
  );
};

const getStatusIndicatorType = (
  status: ChallengeValidationStatus | string
): 'success' | 'error' | 'in-progress' | 'warning' | 'info' => {
  switch (status) {
    case ChallengeValidationStatus.PASSED:
      return 'success';
    case ChallengeValidationStatus.FAILED:
    case ChallengeValidationStatus.ERROR:
      return 'error';
    case ChallengeValidationStatus.SOLVING:
    case ChallengeValidationStatus.WAITING_FOR_LAB:
    case ChallengeValidationStatus.VALIDATING:
      return 'in-progress';
    default:
      return 'info';
  }
};

/**
 * The SDK returns `runAt` as a Date object, but it may be constructed from
 * a unix timestamp in seconds rather than milliseconds. If the year is
 * unreasonably large (>2100), we assume the value was seconds and re-parse.
 */
const formatDate = (date: Date | undefined): string => {
  if (!date) {
    return '-';
  }

  let d = new Date(date);

  if (d.getFullYear() > 2100) {
    d = new Date(d.getTime() / 1000);
  }

  if (d.getFullYear() < 2000) {
    const raw = typeof date === 'number' ? (date as number) : d.getTime();
    d = new Date(raw * 1000);
  }

  return d.toLocaleString();
};

const ChallengeValidation: React.FC<ChallengeValidationProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const { authClient } = useAuth();
  const { addErrorFlashbar } = useFlashbars();

  const challengeId = challenge.challengeId ?? '';

  const [validationRuns, setValidationRuns] = useState<ChallengeValidationState[]>([]);
  const [starting, setStarting] = useState(false);
  const [useCached, setUseCached] = useState(false);
  const [loading, setLoading] = useState(false);

  const jamClient = getJamClient(authClient);

  const getStatusLabel = (status: ChallengeValidationStatus | string): string => {
    switch (status) {
      case ChallengeValidationStatus.SOLVING:
        return t(i18nKeys.challenges.challengeDetails.validation.status.solving);
      case ChallengeValidationStatus.WAITING_FOR_LAB:
        return t(i18nKeys.challenges.challengeDetails.validation.status.waitingForLab);
      case ChallengeValidationStatus.VALIDATING:
        return t(i18nKeys.challenges.challengeDetails.validation.status.validating);
      case ChallengeValidationStatus.PASSED:
        return t(i18nKeys.challenges.challengeDetails.validation.status.passed);
      case ChallengeValidationStatus.FAILED:
        return t(i18nKeys.challenges.challengeDetails.validation.status.failed);
      case ChallengeValidationStatus.ERROR:
        return t(i18nKeys.challenges.challengeDetails.validation.status.error);
      default:
        return status;
    }
  };

  const loadValidationHistory = useCallback(async () => {
    setLoading(true);

    try {
      const result = await jamClient.listChallengeValidations({ challengeId });

      if (isError(result)) {
        addErrorFlashbar(t(i18nKeys.challenges.challengeDetails.validation.errors.loadFailed));
        preProdLogger(result);
        return;
      }

      setValidationRuns(result.items ?? []);
    } catch (err) {
      preProdLogger(err);
    } finally {
      setLoading(false);
    }
  }, [challengeId]);

  useEffect(() => {
    void loadValidationHistory();
  }, [loadValidationHistory]);

  const startValidation = useCallback(async () => {
    setStarting(true);
    try {
      const result = await jamClient.startChallengeValidation({
        challengeId,
        useCached: useCached || undefined,
        version: challenge.version ?? undefined,
      });

      if (isError(result)) {
        addErrorFlashbar(t(i18nKeys.challenges.challengeDetails.validation.errors.startFailed));
        preProdLogger(result);
        setStarting(false);
        return;
      }

      void loadValidationHistory();
      setStarting(false);
    } catch (err) {
      preProdLogger(err);
      setStarting(false);
    }
  }, [challengeId, challenge.version, useCached, loadValidationHistory]);

  const latestRun = validationRuns[0];
  const hasInProgressRun = latestRun && !isTerminalStatus(latestRun.status);
  const hasSuccessfulRun = validationRuns.some((run) => run.status === ChallengeValidationStatus.PASSED);

  const lastRunPassed = latestRun?.status === ChallengeValidationStatus.PASSED;

  useEffect(() => {
    setUseCached(lastRunPassed);
  }, [lastRunPassed]);

  const openDetailModal = (run: ChallengeValidationState) => {
    void NiceModal.show(ChallengeValidationDetailModal, { run, challenge });
  };

  const PAGE_SIZE = 10;

  const { items: paginatedItems, paginationProps } = useCollection(validationRuns, {
    pagination: { pageSize: PAGE_SIZE },
    sorting: {},
  });

  return (
    <Container
      header={<Header variant="h2">{t(i18nKeys.challenges.challengeDetails.validation.heading)}</Header>}
      data-testid="validation-container"
    >
      <SpaceBetween size="m" direction="vertical">
        {/* Validation runs table */}
        <Table
          filter={
            <SpaceBetween size="s" direction="horizontal" alignItems="center">
              <Button
                variant="primary"
                onClick={() => void startValidation()}
                loading={starting}
                disabled={!!hasInProgressRun}
                disabledReason={t(i18nKeys.challenges.challengeDetails.validation.errors.validationInProgress)}
                data-testid="start-validation-button"
              >
                {t(i18nKeys.challenges.challengeDetails.validation.buttons.startValidation)}
              </Button>

              <Checkbox
                checked={useCached}
                onChange={({ detail }) => setUseCached(detail.checked)}
                disabled={!hasSuccessfulRun}
                data-testid="use-cached-checkbox"
              >
                {t(i18nKeys.challenges.challengeDetails.validation.buttons.useCachedCommands)}
              </Checkbox>
            </SpaceBetween>
          }
          preferences={
            <AutoRefreshDropdown onRefresh={loadValidationHistory} data-testid="refresh-validation-dropdown" />
          }
          columnDefinitions={[
            {
              id: 'runAt',
              header: t(i18nKeys.challenges.challengeDetails.validation.labels.runAt),
              cell: (item: ChallengeValidationState) => formatDate(item.runAt),
              width: 180,
            },
            {
              id: 'status',
              header: t(i18nKeys.challenges.challengeDetails.validation.labels.status),
              cell: (item: ChallengeValidationState) => (
                <StatusIndicator type={getStatusIndicatorType(item.status)}>
                  {getStatusLabel(item.status)}
                </StatusIndicator>
              ),
              width: 150,
            },
            {
              id: 'version',
              header: t(i18nKeys.challenges.challengeDetails.validation.labels.version),
              cell: (item: ChallengeValidationState) => item.challengeVersion ?? '-',
              width: 80,
            },
            {
              id: 'tasks',
              header: t(i18nKeys.challenges.challengeDetails.validation.labels.progress),
              cell: (item: ChallengeValidationState) =>
                `${String(item.completedTasks ?? 0)} / ${String(item.totalTasks ?? '?')}`,
              width: 100,
            },
            {
              id: 'attempts',
              header: t(i18nKeys.challenges.challengeDetails.validation.labels.attempts),
              cell: (item: ChallengeValidationState) => item.attemptNumber ?? '-',
              width: 100,
            },
            {
              id: 'actions',
              header: '',
              cell: (item: ChallengeValidationState) => (
                <Button variant="inline-link" onClick={() => openDetailModal(item)} data-testid="view-details-button">
                  {t(i18nKeys.challenges.challengeDetails.validation.buttons.viewDetails)}
                </Button>
              ),
              width: 120,
            },
          ]}
          items={paginatedItems}
          loading={loading}
          loadingText={t(i18nKeys.challenges.challengeDetails.validation.table.loading)}
          skeleton={{ totalRows: 10 }}
          variant="embedded"
          data-testid="validation-runs-table"
          stickyColumns={{ last: 1 }}
          pagination={validationRuns.length > PAGE_SIZE ? <Pagination {...paginationProps} /> : null}
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              {t(i18nKeys.challenges.challengeDetails.validation.empty)}
            </Box>
          }
        />
      </SpaceBetween>
    </Container>
  );
};

export default ChallengeValidation;
